import { useEffect, useRef, useState } from 'react'
import AddTraspButton from '../../firstTimeUserExperience/commonCom/AddTraspButton';
import { GetImages } from '../../../utils/GetImages';
import CustomInputs from '../Inputs';
import CustomTooltip from '../CustomTooltip';
import { Form } from 'react-bootstrap';

// notify is optional: standalone consumers (e.g. the font-size unit tests, MaintenanceBannerDrawer's
// content-only usage) don't need the "nothing to remove"/"only images allowed" feedback toasts below.
// The four toggleable formatting commands the toolbar reflects live active/inactive state for --
// queried off the real browser selection state, not just "which button was last clicked" (a selection
// can already be bold when the cursor lands in it without ever clicking Bold in this session).
const TOGGLE_FORMAT_COMMANDS = ["bold", "italic", "underline", "strikeThrough"] as const;

const EditorToolBar = ({ editorRef, handleFileChange, disableAttachments = false, notify = undefined }) => {
    const [activeFormats, setActiveFormats] = useState<Set<string>>(new Set());
    const savedRangeRef = useRef<Range | null>(null);
    const [fontSizeValue, setFontSizeValue] = useState("");

    const fontSizes = Array.from({ length: 14 }, (_, i) => i + 1); // Sizes 1 to 14

    // document.queryCommandState throws in some browsers for an unsupported command/when the document
    // doesn't have focus, and isn't implemented at all in jsdom (same gap as execCommand -- see the
    // font-size test file's own comment on this) -- never let a read of "is this currently active"
    // crash the toolbar.
    const refreshActiveFormats = () => {
        const next = new Set<string>();
        TOGGLE_FORMAT_COMMANDS.forEach((command) => {
            try {
                if (document.queryCommandState(command)) next.add(command);
            } catch {
                // unsupported/unavailable -- leave this command out of the active set.
            }
        });
        setActiveFormats(next);
    };

    // Caches the editor's live selection Range on mouseup/keyup -- clicking the font-size <select>
    // below can clear window.getSelection() before onChange fires, so we need our own last-known
    // selection rather than reading it fresh at that point. Also refreshes which formatting buttons
    // should show as active for wherever the cursor/selection now is (bug/ui-bugfix-batch9 follow-up --
    // Bold/Italic/etc previously never visually reflected whether the selected text actually was bold).
    useEffect(() => {
        const editor = editorRef?.current;
        if (!editor) return;
        const saveSelection = () => {
            const selection = window.getSelection();
            if (selection && selection.rangeCount > 0 && editor.contains(selection.anchorNode)) {
                savedRangeRef.current = selection.getRangeAt(0).cloneRange();
            }
            refreshActiveFormats();
        };
        // A mouse drag-selection released outside the editor never fires the editor's own mouseup, which left
        // a stale saved range and made the font-size dropdown apply to the wrong text (or nothing).
        const saveSelectionOnChange = () => {
            const selection = window.getSelection();
            if (selection && selection.rangeCount > 0 && editor.contains(selection.anchorNode)) {
                savedRangeRef.current = selection.getRangeAt(0).cloneRange();
            }
        };
        editor.addEventListener("mouseup", saveSelection);
        editor.addEventListener("keyup", saveSelection);
        document.addEventListener("selectionchange", saveSelectionOnChange);
        return () => {
            editor.removeEventListener("mouseup", saveSelection);
            editor.removeEventListener("keyup", saveSelection);
            document.removeEventListener("selectionchange", saveSelectionOnChange);
        };
    }, [editorRef]);

    const applyCommand = (command, value = null) => {
        document.execCommand(command, false, value);
        // execCommand changes the live selection's format state synchronously -- refresh right after so a
        // toggle (e.g. clicking Bold on) is reflected immediately, not only on the next click/selection change.
        refreshActiveFormats();
    };

    // A real <button> (unlike a plain <img>) steals focus from the contentEditable body on mousedown in
    // Chromium -- clicking Bold/Italic/etc previously knocked focus out of the editor, forcing the user to
    // click back into the body before they could keep typing. preventDefault on mousedown (not click) stops
    // the browser's default focus-shift before it happens, without blocking the click/onClick that follows.
    const preventEditorBlur = (e: React.MouseEvent) => e.preventDefault();

    // document.execCommand("fontSize", ...) only recognizes the legacy HTML font-size levels 1-7
    // (<font size="N">) -- this dropdown goes to 14, so anything above 7 is silently clamped to
    // level 7 by the browser, and re-applying a *smaller* level after a larger one is a well-known
    // cross-browser unreliability of this deprecated command.
    //
    // A hand-rolled range.extractContents()/insertNode() replacement corrupts the editor's block
    // structure whenever the selection spans multiple lines or list items (contentEditable creates
    // one <div>/<li> per line, and extractContents() clones each partially-selected block rather
    // than merging them, so e.g. a two-line selection becomes four lines). Instead, let the browser
    // itself handle the (hard) job of correctly splitting/wrapping a multi-node selection via
    // execCommand at a single legacy level (7), then re-tag only the <font> elements THIS call just
    // created (diffed against what already existed) with our own literal pixel size.
    const FONT_SIZE_PX_SCALE = 3;
    const FONT_SIZE_PX_BASE = 8; // dropdown values 1-14 map to 11px-50px, a visibly distinct range
    const changeFontSize = (e) => {
        const size = Number(e.target.value);
        // Reset to the placeholder so picking the same size again (for a new selection) still fires onChange.
        setFontSizeValue("");
        const editor = editorRef?.current;
        if (!size || !editor) return;
        const selection = window.getSelection();
        if (!selection) return;
        // Choosing an option with the mouse moves focus to the <select>; execCommand only acts on a selection
        // inside a focused editing host, so return focus to the editor before restoring the saved range.
        editor.focus({ preventScroll: true });
        if (savedRangeRef.current && editor.contains(savedRangeRef.current.commonAncestorContainer)) {
            selection.removeAllRanges();
            selection.addRange(savedRangeRef.current);
        }
        if (selection.rangeCount === 0 || selection.getRangeAt(0).collapsed) return;
        const range = selection.getRangeAt(0);
        if (!editor.contains(range.commonAncestorContainer)) return;

        const preExistingLevel7Fonts = new Set(editor.querySelectorAll('font[size="7"]'));
        document.execCommand("fontSize", false, "7");

        const pxSize = size * FONT_SIZE_PX_SCALE + FONT_SIZE_PX_BASE;
        editor.querySelectorAll('font[size="7"]').forEach((fontEl) => {
            if (preExistingLevel7Fonts.has(fontEl)) return;
            const span = document.createElement("span");
            span.style.fontSize = `${pxSize}px`;
            while (fontEl.firstChild) span.appendChild(fontEl.firstChild);
            fontEl.replaceWith(span);
        });

        // execCommand doesn't fire a React-visible "input" event -- controlled forms that track
        // dirty state off the editor's own input handler (e.g. the Maintenance Banner drawer) need
        // this nudge or Save stays disabled after a font-size-only edit.
        editor.dispatchEvent(new Event("input", { bubbles: true }));

        if (selection.rangeCount > 0) {
            savedRangeRef.current = selection.getRangeAt(0).cloneRange();
        }
    };

    // "Insert Photo" always embeds the file inline in the editor body via FileReader.readAsDataURL, matching
    // Gmail's own compose behavior (an inserted/pasted image appears inline in the message text, not as a
    // separate attachment card below it) -- this used to branch into the attachment list instead for any
    // consumer with attachments available (Compose.tsx's Gmail flow), which was reported as "tottally wrong"
    // against the real Gmail reference. accept="image/*" only hints the OS file picker, so a user could still
    // pick a non-image (e.g. a PDF) -- the imageFiles filter below (independent of the inline-vs-attachment
    // question) is what actually fixes that "corrupted PDF icon" case, by never handing a non-image file to
    // insertImageInline in the first place.
    const insertImageInline = (file: File) => {
        const reader = new FileReader();
        reader.onload = (e) => {
            if (editorRef.current) {
                const img = document.createElement("img");
                img.src = e.target.result as string;
                img.alt = file.name;
                img.style.maxWidth = "100%";
                img.style.margin = "10px 0";
                editorRef.current.appendChild(img);
            }
        };
        reader.readAsDataURL(file);
    };

    const onInsertPhotoChange = (e) => {
        const files = Array.from(e.target.files ?? []) as File[];
        const imageFiles = files.filter((file) => file.type.startsWith("image/"));
        if (imageFiles.length < files.length) {
            notify?.("Only image files can be inserted as a photo");
        }
        imageFiles.forEach(insertImageInline);
        e.target.value = "";
    };

    // document.execCommand("unlink") is a correct no-op whenever the current selection isn't inside/
    // touching a hyperlink -- not actually broken, but a bare "X" with no feedback looks broken when it
    // silently does nothing. Give the user an explicit reason instead of assuming they'll infer it.
    const selectionHasLink = () => {
        const selection = window.getSelection();
        const editor = editorRef?.current;
        if (!editor) return false;
        const liveRange =
            selection && selection.rangeCount > 0 && editor.contains(selection.anchorNode)
                ? selection.getRangeAt(0)
                : null;
        const range = liveRange ?? savedRangeRef.current;
        if (!range) return false;
        let node: Node | null = range.commonAncestorContainer;
        while (node && node !== editor) {
            if (node.nodeType === 1 && (node as HTMLElement).tagName === "A") return true;
            node = node.parentNode;
        }
        return false;
    };

    const handleUnlink = () => {
        if (!selectionHasLink()) {
            notify?.("Select a link to remove it");
            return;
        }
        applyCommand("unlink");
    };

    return (
        <div className="Editor_toolbar">
            <select className="toolbar-select" value={fontSizeValue} onChange={changeFontSize} title="Font Size">
                <option value="" disabled hidden>Size</option>
                {fontSizes.map((size) => (
                    <option key={size} value={size}>
                        {size}
                    </option>
                ))}
            </select>
            <div className="divider"></div>
            <input
                type="color"
                className="toolbar-color-picker"
                onChange={(e) => applyCommand("foreColor", e.target.value)}
                title="Text Color"
            />
            <div className="divider"></div>
            <span title="Bold" onMouseDown={preventEditorBlur}>
                <AddTraspButton
                    type={"button"}
                    width={0}
                    bname={<b>B</b>}
                    textclass={activeFormats.has("bold") ? "sub_heading grey fw-600 toolbar-button-active" : "sub_heading grey fw-600"}
                    className="px-1 toolbar-button"
                    onClick={() => applyCommand("bold")}
                    border={false}
                />
            </span>
            <span title="Italic" onMouseDown={preventEditorBlur}>
                <AddTraspButton
                    type={"button"}
                    width={0}
                    bname={<i>I</i>}
                    textclass={activeFormats.has("italic") ? "sub_heading grey fw-600 toolbar-button-active" : "sub_heading grey fw-600"}
                    className="px-1 toolbar-button"
                    onClick={() => applyCommand("italic")}
                    border={false}
                />
            </span>
            <span title="Strikethrough" onMouseDown={preventEditorBlur}>
                <AddTraspButton
                    type={"button"}
                    width={0}
                    bname={<s>S</s>}
                    textclass={activeFormats.has("strikeThrough") ? "sub_heading grey fw-600 toolbar-button-active" : "sub_heading grey fw-600"}
                    className="p-0"
                    onClick={() => applyCommand("strikeThrough")}
                    border={false}
                />
            </span>
            <span title="Underline" onMouseDown={preventEditorBlur}>
                <AddTraspButton
                    type={"button"}
                    width={0}
                    bname={<u>U</u>}
                    className="p-0"
                    textclass={activeFormats.has("underline") ? "sub_heading grey fw-600 toolbar-button-active" : "sub_heading grey fw-600"}
                    onClick={() => applyCommand("underline")}
                    border={false}
                />
            </span>
            <div className="divider"></div>
            <CustomTooltip content="Align Left" children={
                <img
                    src={GetImages.GreyLeftAlign}
                    className="iconstyle clickable color-white-icon"
                    alt="Align Left"
                    onClick={() => applyCommand("justifyLeft")}
                />
            } />
            <CustomTooltip content="Align Center" children={
                <img
                    src={GetImages.GreyCenterAlign}
                    className="iconstyle clickable color-white-icon"
                    alt="Align Center"
                    onClick={() => applyCommand("justifyCenter")}
                />
            } />
            <CustomTooltip content="Align Right" children={
                <img
                    src={GetImages.GreyRightAlign}
                    className="iconstyle clickable color-white-icon"
                    alt="Align Right"
                    onClick={() => applyCommand("justifyRight")}
                />
            } />
            <div className="divider"></div>
            <CustomTooltip content="Numbered List" children={
                <img
                    src={GetImages.GreyNumberList}
                    className="iconstyle clickable color-white-icon"
                    alt="Numbered List"
                    onClick={() => applyCommand("insertOrderedList")}
                />
            } />
            <CustomTooltip content="Bullet List" children={
                <img
                    src={GetImages.GreyDotList}
                    className="iconstyle clickable color-white-icon"
                    alt="Bullet List"
                    onClick={() => applyCommand("insertUnorderedList")}
                />
            } />
            <div className="divider"></div>
            <CustomTooltip content="Insert Link" children={
                <img
                    src={GetImages.GreyLink}
                    className="iconstyle clickable color-white-icon"
                    alt="Insert Link"
                    onClick={() => applyCommand("createLink", window.getSelection()?.toString())}
                />
            } />
            <span title="Remove Link" onMouseDown={preventEditorBlur}>
                <AddTraspButton
                    type={"button"}
                    width={0}
                    bname={"X"}
                    className="p-0"
                    imageValue={undefined}
                    iconPosition={"right"}
                    onClick={handleUnlink}
                    textclass={"sub_heading grey fw-600"}
                    border={false}
                />
            </span>
            <Form.Group controlId="formFile" className="mb-0">
                <Form.Control
                    type="file"
                    accept="image/*"
                    onChange={onInsertPhotoChange}
                    multiple
                    style={{ display: "none" }}
                />
                <CustomTooltip content="Insert Photo" children={
                    <img
                        src={GetImages.GreyImageicon}
                        className="iconstyle clickable color-white-icon"
                        alt="Insert Photo"
                        onClick={() => document.getElementById("formFile").click()}
                    />
                } />
            </Form.Group>
            {!disableAttachments && (
                <>
                    <div className="divider"></div>
                    <div className="attachment-container">
                        <CustomTooltip content="Attach File" children={
                            <label htmlFor="file-input">
                                <img src={GetImages.BlackAttachemnet} className="iconstyle color-white-icon" alt="Attach File" />
                                <CustomInputs
                                    type="file"
                                    id="file-input"
                                    width={0}
                                    multiple
                                    onChange={handleFileChange}
                                    myClassName="email-file-input"
                                />
                            </label>
                        } />
                    </div>
                </>
            )}
        </div>
    )
}

export default EditorToolBar